<template>
  <StatsWidget
    :title="$t('stats_dashboard.products.title')"
    :description="$t('stats_dashboard.products.description')"
    icon="box"
    tone="violet"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :span-full="true"
    :empty-message="$t('stats_dashboard.products.empty')"
    @refresh="$emit('refresh')"
  >
    <template #actions>
      <div class="stats-products__tabs" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="stats-products__tab"
          :class="{ 'stats-products__tab--active': activeTab === tab.value }"
          :aria-selected="activeTab === tab.value"
          role="tab"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </div>
    </template>

    <div class="stats-products">
      <!-- Totals + concentration strip -->
      <div class="stats-products__totals">
        <div class="stats-products__total-card stats-products__total-card--sold">
          <span class="stats-products__total-label">
            {{ $t('stats_dashboard.products.totals.sold') }}
          </span>
          <span class="stats-products__total-value">
            {{ formatCurrency(totals.sold_value) }}
          </span>
        </div>
        <div class="stats-products__total-card stats-products__total-card--invoiced">
          <span class="stats-products__total-label">
            {{ $t('stats_dashboard.products.totals.invoiced') }}
          </span>
          <span class="stats-products__total-value">
            {{ formatCurrency(totals.invoiced_value) }}
          </span>
        </div>
        <div class="stats-products__total-card">
          <span class="stats-products__total-label">
            {{ $t('stats_dashboard.products.totals.products_with_id') }}
          </span>
          <span class="stats-products__total-value">
            {{ formatInteger(totals.products_with_id) }}
          </span>
        </div>
        <div v-if="concentrationLabel" class="stats-products__concentration">
          <BaseIcon name="bullseye" size="12" />
          <span>{{ concentrationLabel }}</span>
        </div>
      </div>

      <!-- Tab: top products -->
      <ul v-show="activeTab === 'products'" class="stats-products__list">
        <li
          v-for="(product, idx) in byProduct"
          :key="product.product_id || `unknown-${idx}`"
          class="stats-products__row"
          :class="{ 'stats-products__row--unknown': !product.product_id }"
        >
          <span class="stats-products__rank">{{ idx + 1 }}</span>
          <div class="stats-products__product">
            <div class="stats-products__product-head">
              <span class="stats-products__product-name">
                {{ product.title || $t('stats_dashboard.products.unknown_product') }}
              </span>
              <span
                v-if="product.share_pct != null"
                class="stats-products__share"
              >
                {{ formatPercent(product.share_pct) }}
              </span>
            </div>

            <div class="stats-products__metric">
              <span class="stats-products__metric-label stats-products__metric-label--sold">
                {{ $t('stats_dashboard.products.legend.sold') }}
              </span>
              <div class="stats-products__bar">
                <div
                  class="stats-products__bar-fill stats-products__bar-fill--sold"
                  :style="{ width: barWidth(product.sold_value, maxSoldValue) + '%' }"
                />
              </div>
              <span class="stats-products__metric-value">
                {{ formatCurrency(product.sold_value) }}
              </span>
            </div>

            <div class="stats-products__metric">
              <span class="stats-products__metric-label stats-products__metric-label--invoiced">
                {{ $t('stats_dashboard.products.legend.invoiced') }}
              </span>
              <div class="stats-products__bar">
                <div
                  class="stats-products__bar-fill stats-products__bar-fill--invoiced"
                  :style="{ width: barWidth(product.invoiced_value, maxSoldValue) + '%' }"
                />
              </div>
              <span class="stats-products__metric-value">
                {{ formatCurrency(product.invoiced_value) }}
              </span>
            </div>

            <div class="stats-products__breakdown">
              <span>
                {{ $t('stats_dashboard.products.sold_breakdown', {
                  units: formatInteger(product.sold_units),
                  proposals: formatInteger(product.sold_proposals),
                }) }}
              </span>
            </div>
          </div>
        </li>
      </ul>

      <!-- Tab: by category -->
      <div v-show="activeTab === 'categories'" class="stats-products__categories">
        <div v-if="byCategory.length" class="stats-products__category-grid">
          <div class="stats-products__donut">
            <Doughnut :data="donutData" :options="donutOptions" />
            <div class="stats-products__donut-center">
              <span class="stats-products__donut-center-label">
                {{ $t('stats_dashboard.products.donut_center') }}
              </span>
              <span class="stats-products__donut-center-value">
                {{ formatCurrencyCompact(totals.sold_value) }}
              </span>
            </div>
          </div>

          <ul class="stats-products__category-list">
            <li
              v-for="(cat, idx) in byCategory"
              :key="cat.category_id || `nocat-${idx}`"
              class="stats-products__category-row"
              :class="{ 'stats-products__category-row--nocat': !cat.category_id }"
            >
              <span
                class="stats-products__category-dot"
                :style="{ background: categoryColor(idx) }"
              />
              <span class="stats-products__category-name">
                {{ cat.title || $t('stats_dashboard.products.no_category') }}
              </span>
              <span class="stats-products__category-share">
                {{ formatPercent(cat.share_pct) }}
              </span>
              <span class="stats-products__category-value">
                {{ formatCurrency(cat.sold_value) }}
              </span>
            </li>
          </ul>
        </div>
        <p v-else class="stats-products__empty-tab">
          {{ $t('stats_dashboard.products.empty') }}
        </p>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Products widget (§C of the 2026-05-12 delta).
   *
   * Two views in one widget, switchable via tabs in the header:
   *   • "Top producten" — ranked product list with parallel sold/
   *     invoiced bars, share-% badge, and a units/proposals breakdown.
   *   • "Per categorie" — doughnut chart with a colour-coded legend
   *     listing share-% and absolute sold-value per category.
   *
   * Above both views: a totals strip showing total sold, total
   * invoiced, unique-product count, and a concentration hint
   * ("Top 3 = 65% van verkocht") so the user immediately sees how
   * concentrated the revenue is.
   */
  import { computed, getCurrentInstance, ref } from 'vue';
  import { Doughnut } from 'vue-chartjs';
  import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
  } from 'chart.js';

  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    formatCurrency,
    formatCurrencyCompact,
    formatInteger,
    formatPercent,
  } from '@/utils/statsFormat';

  ChartJS.register(ArcElement, Tooltip, Legend);

  // Distinct hues so neighbouring slices don't blend on the donut.
  // The "no category" / "unknown" buckets get a neutral grey appended
  // at runtime so they don't compete visually with the real data.
  const CATEGORY_PALETTE = [
    '#3b82f6', // blue-500
    '#a855f7', // violet-500
    '#10b981', // emerald-500
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#06b6d4', // cyan-500
    '#84cc16', // lime-500
    '#f97316', // orange-500
    '#ec4899', // pink-500
    '#6366f1', // indigo-500
  ];
  const NEUTRAL_GREY = '#cbd5e1'; // slate-300

  export default {
    name: 'StatsProducts',

    components: { StatsWidget, BaseIcon, Doughnut },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const activeTab = ref('products');

      const tabs = computed(() => [
        { value: 'products', label: $t('stats_dashboard.products.tab_products') },
        {
          value: 'categories',
          label: $t('stats_dashboard.products.tab_categories'),
        },
      ]);

      const byProduct = computed(() => props.widget.data?.by_product || []);
      const byCategory = computed(() => props.widget.data?.by_category || []);
      const totals = computed(() => props.widget.data?.totals || {});

      const hasData = computed(
        () => byProduct.value.length > 0 || byCategory.value.length > 0
      );

      // Bars are sized relative to the highest sold value in the list
      // so the visual ranking matches the numeric ranking. Using sold
      // (not invoiced) as the reference keeps invoiced bars subordinate
      // — visually telling: "we sold this much, but only invoiced this".
      const maxSoldValue = computed(() => {
        const values = byProduct.value.map((p) => Number(p.sold_value) || 0);
        return values.length ? Math.max(...values) : 0;
      });

      const barWidth = (value, max) => {
        const v = Number(value) || 0;
        if (!max || max <= 0) return 0;
        // Reserve a sliver so non-zero values are visible even at the
        // tail of the distribution.
        return Math.max(v > 0 ? 2 : 0, Math.round((v / max) * 100));
      };

      // 80/20 concentration sentence. We pick the smallest top-N that
      // already covers ≥ 80% of total sold; if that number is 1 we
      // use the singular sentence variant for nicer copy.
      const concentrationLabel = computed(() => {
        const total = Number(totals.value.sold_value) || 0;
        if (total <= 0 || byProduct.value.length === 0) return '';
        let cumulative = 0;
        let n = 0;
        for (const p of byProduct.value) {
          cumulative += Number(p.sold_value) || 0;
          n += 1;
          if (cumulative / total >= 0.8) break;
        }
        const sharePct = (cumulative / total) * 100;
        const formattedShare = formatPercent(sharePct);
        if (n === 1) {
          return $t('stats_dashboard.products.concentration.single', {
            share: formattedShare,
          });
        }
        return $t('stats_dashboard.products.concentration.top_n', {
          n,
          share: formattedShare,
        });
      });

      const categoryColor = (idx) => {
        const cat = byCategory.value[idx];
        if (cat && !cat.category_id) return NEUTRAL_GREY;
        return CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length];
      };

      const donutData = computed(() => ({
        labels: byCategory.value.map(
          (c) => c.title || $t('stats_dashboard.products.no_category')
        ),
        datasets: [
          {
            data: byCategory.value.map((c) => Number(c.sold_value) || 0),
            backgroundColor: byCategory.value.map((_, idx) => categoryColor(idx)),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 6,
          },
        ],
      }));

      const donutOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
          // We render a richer legend in the side panel (with values
          // and percentages), so suppress the built-in one.
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#fff',
            bodyColor: '#e2e8f0',
            padding: 10,
            callbacks: {
              label: (ctx) => {
                const value = ctx.raw ?? 0;
                const cat = byCategory.value[ctx.dataIndex];
                const share = cat?.share_pct;
                const sharePart = share != null ? ` (${formatPercent(share)})` : '';
                return `${ctx.label}: ${formatCurrency(value)}${sharePart}`;
              },
            },
          },
        },
      }));

      return {
        activeTab,
        tabs,
        byProduct,
        byCategory,
        totals,
        hasData,
        maxSoldValue,
        barWidth,
        concentrationLabel,
        categoryColor,
        donutData,
        donutOptions,
        formatCurrency,
        formatCurrencyCompact,
        formatInteger,
        formatPercent,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-products {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;

    // Tab buttons live in the widget header's #actions slot.
    &__tabs {
      display: inline-flex;
      gap: 0.125rem;
      padding: 0.1875rem;
      background: var(--slate-100);
      border-radius: 0.5rem;
    }

    &__tab {
      padding: 0.3125rem 0.75rem;
      border: 0;
      background: transparent;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-600);
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        color 0.15s ease;

      @include hover {
        color: var(--slate-800);
      }

      &--active {
        background: var(--white);
        color: var(--slate-900);
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
      }
    }

    // ---------- Totals strip ----------

    &__totals {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
      align-items: center;
      gap: 0.625rem;

      @include lg-down {
        grid-template-columns: repeat(2, minmax(0, 1fr));

        > :last-child {
          grid-column: span 2;
        }
      }

      @include sm-down {
        grid-template-columns: 1fr;

        > :last-child {
          grid-column: span 1;
        }
      }
    }

    &__total-card {
      padding: 0.625rem 0.875rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;

      &--sold {
        background: linear-gradient(180deg, #eff6ff, #f8fafc);
        border-color: #bfdbfe;
      }

      &--invoiced {
        background: linear-gradient(180deg, #fff7ed, #f8fafc);
        border-color: #fed7aa;
      }
    }

    &__total-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__total-value {
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }

    &__concentration {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.4375rem 0.75rem;
      background: var(--brand-50);
      border: 1px solid var(--brand-100, #dbeafe);
      border-radius: 9999px;
      color: var(--brand-700);
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;

      .icon {
        color: var(--brand-500);
      }
    }

    // ---------- Top-products list ----------

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1.75rem 1fr;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-100);
      border-radius: 0.625rem;
      transition: border-color 0.15s ease;

      @include hover {
        border-color: var(--slate-200);
      }

      &--unknown {
        background: var(--slate-50);
        border-style: dashed;
      }
    }

    &__rank {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background: var(--slate-100);
      color: var(--slate-700);
      font-size: 0.75rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
      align-self: flex-start;
      margin-top: 0.0625rem;
    }

    &__product {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      min-width: 0;
    }

    &__product-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__product-name {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__share {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--brand-700);
      background: var(--brand-50);
      padding: 0.125rem 0.4375rem;
      border-radius: 9999px;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }

    &__metric {
      display: grid;
      grid-template-columns: 5.5rem 1fr 5rem;
      align-items: center;
      gap: 0.625rem;

      @include sm-down {
        grid-template-columns: 5rem 1fr 4.5rem;
        gap: 0.4375rem;
      }
    }

    &__metric-label {
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);

      &--sold {
        color: #2563eb;
      }
      &--invoiced {
        color: #ea580c;
      }
    }

    &__bar {
      height: 6px;
      background: var(--slate-100);
      border-radius: 9999px;
      overflow: hidden;
    }

    &__bar-fill {
      height: 100%;
      border-radius: 9999px;
      transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);

      &--sold {
        background: linear-gradient(90deg, #60a5fa, #2563eb);
      }
      &--invoiced {
        background: linear-gradient(90deg, #fdba74, #ea580c);
      }
    }

    &__metric-value {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      text-align: right;
    }

    &__breakdown {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    // ---------- Categories tab ----------

    &__categories {
      flex: 1;
    }

    &__category-grid {
      display: grid;
      grid-template-columns: minmax(160px, 220px) 1fr;
      gap: 1.25rem;
      align-items: center;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__donut {
      position: relative;
      width: 100%;
      max-width: 220px;
      aspect-ratio: 1;
      margin: 0 auto;
    }

    &__donut-center {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      gap: 0.125rem;
    }

    &__donut-center-label {
      font-size: 0.625rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__donut-center-value {
      font-size: 1rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__category-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__category-row {
      display: grid;
      grid-template-columns: 0.625rem 1fr auto auto;
      align-items: center;
      gap: 0.625rem;
      padding: 0.4375rem 0.5rem;
      border-radius: 0.375rem;
      transition: background-color 0.15s ease;

      @include hover {
        background: var(--slate-50);
      }

      &--nocat .stats-products__category-name {
        color: var(--slate-500);
        font-style: italic;
      }
    }

    &__category-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__category-name {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__category-share {
      font-size: 0.75rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
      min-width: 3rem;
      text-align: right;
    }

    &__category-value {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      min-width: 5.5rem;
      text-align: right;
    }

    &__empty-tab {
      margin: 0;
      padding: 1.5rem;
      text-align: center;
      color: var(--slate-500);
      font-size: 0.8125rem;
      background: var(--slate-50);
      border: 1px dashed var(--slate-200);
      border-radius: 0.5rem;
    }
  }
</style>
